<script setup>
import { computed } from 'vue'

import { useODataSource, useLookup } from '@/composables/data-grid'
import { useRegistryStore } from '@/stores/registry.store'

import GridDropDownBox from '@/components/shared/GridDropDownBox.vue'

const props = defineProps({
  data: {
    type: Object,
    default: null,
  },
  value: {
    type: Number,
    default: null,
  },
  type: {
    type: Number,
    default: null,
  },
  excludeSelf: {
    type: Boolean,
    default: false,
  },
  showClearButton: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
})

const registryStore = useRegistryStore()

const defaultDataSource = useODataSource(registryStore.api.getAbsoluteUrl() + registryStore.api.subjectsUrl, {
  searchExpr: ['name', 'code', 'name_ext']
})

const computedDataSource = computed(() => {
  const filters = []
  const ds = defaultDataSource

  if (props.type) {
    filters.push(['type', '=', props.type])
  }
  if (props.excludeSelf && !!props.data.data.id) {
    filters.push(['id', '<>', props.data.data.id])
  }

  const finalFilter = filters.flatMap((filter, index) => {
    return index === 0 ? [filter] : ['and', filter]
  })

  ds.filter(finalFilter.length ? finalFilter : null)
  return ds
})

const emit = defineEmits({
  valueChanged: (e) => true,
})

</script>

<template>
  <div>
    <grid-drop-down-box
      :value="props.value || props.data?.value"
      :data-source="computedDataSource"
      :show-clear-button="props.showClearButton"
      :disabled="props.disabled"
      :columns="[
        { dataField: 'code', caption: $t('common.code'), width: '5rem', sortOrder: 'asc' },
        {
          dataField: 'type', caption: $t('common.subjectSubType'), width: '10rem',
          cellTemplate: 'cellTemplate-type', alignment: 'center',
          lookup: useLookup({ store: registryStore.getLocalizedSubjectEntityTypes(), displayExpr: 'name' }),
        },
        {
          dataField: 'customer_privacy_check', caption: $t('common.privacy'),
          cellTemplate: 'cellTemplate-customer_privacy_check', width: '5rem',
        },
        {
          dataField: 'name', caption: $t('common.name'),
          calculateCellValue: (rowData) => {
            let name = rowData.name
            if (rowData.name_ext) { name += ` ${rowData.name_ext}` }
            return name
          },
        },
        { dataField: 'phone', caption: $t('common.phone') },
        { dataField: 'vat_number', caption: $t('common.vatNumber') },
        { dataField: 'tax_code', caption: $t('common.taxCode') },
        { dataField: 'address.city_name', caption: $t('common.city') },
        { dataField: 'address.street', caption: $t('common.street') },
      ]"
      :drop-down-options="{ width: '66rem', maxWidth: 'calc(100vw - 2rem)', height: '30rem' }"
      @value-changed="(e) => {
        props.data.setValue(e)
        emit('valueChanged', { value: e})
      }"
    >
      <template #cellTemplate-customer_privacy_check="{ data: templateData }">
        <q-chip
          v-if="templateData.data.customer_id && !templateData.data.customer_privacy_check"
          size="sm"
          text-color="white"
          class="q-ma-none"
          :label="$t('common.privacy')"
          color="red"
        />
      </template>
      <template #cellTemplate-type="{ data: templateData }">
        <q-chip
          size="sm"
          text-color="white"
          class="q-ma-none"
          :label="registryStore.getLocalizedSubjectEntityTypes().find(x => x.id === templateData.data.type)?.name"
          :color="registryStore.getLocalizedSubjectEntityTypes().find(x => x.id === templateData.data.type)?.color"
        />
      </template>
    </grid-drop-down-box>
  </div>
</template>
